import { Badge, Box, Flex, Icon, Tabs, Text, VStack } from '@chakra-ui/react'
import { MessageSquareText, MessageSquareX, Phone } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import TrimmedIncomingCallTone from '~/assets/sounds/trimmed-ringtone.mp3'
import { Ringtone, useSettingsState } from '~/atoms/SettingsState'
import { useUserState } from '~/atoms/UserState'
import { useRelayedCallAnnouncements } from '~/hooks/useCallAnnouncementRelay'
import {
  AnnouncedCall,
  useCallStateAnnouncements,
} from '~/hooks/useCallStateAnnouncements'
import { useIsPartner, useShowTexting } from '~/hooks/useCurrentPartner'
import { useSharedVariable } from '~/hooks/useSharedVariable'
import { useWindowDimensions } from '~/hooks/useWindowDimensions'
import { exampleTexts } from '~/mocks/popupMockData'
import { PopupHeader } from '~/pages/Popup/PopupHeader'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'
import {
  CallQueueItem,
  isPreConnectedStatus,
  MessageQueueItem,
  PhoneCall,
  PhoneCallStatus,
  PopupQueueItem,
  PopupQueueItemTypes,
} from '@mangovoice/types'

import { CallList } from './CallList'
import { MessageList } from './MessageList'

interface StyledTabProps {
  children: React.ReactNode
  value: string
  [key: string]: any
}

interface CountBadgeProps {
  count: number
}

const StyledTab: React.FC<StyledTabProps> = ({ children, value, ...props }) => (
  <Tabs.Trigger
    value={value}
    height="40px"
    borderRadius="lg"
    color="fg.muted"
    fontSize="14px"
    _selected={{
      bg: 'bg.muted',
      color: 'fg.default',
    }}
    {...props}
  >
    {children}
  </Tabs.Trigger>
)

const CountBadge: React.FC<CountBadgeProps> = ({ count }) => (
  <Badge
    bg="brand.solid"
    color="brand.contrast"
    mr={2}
    width="1.25rem"
    height="1.25rem"
    borderRadius="md"
    fontSize="10px"
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    {count}
  </Badge>
)

interface EmptyStateProps {
  icon: React.ElementType
  title: string
  description: string
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
}) => (
  <VStack gap={4} justifyContent="center" alignItems="center" mt="60px">
    <Box
      bg="bg.muted"
      borderRadius="full"
      p={4}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Icon
        as={icon}
        boxSize="48px"
        fill="brand.solid"
        strokeWidth={0.5}
        stroke="bg.panel"
      />
    </Box>
    <VStack gap={2}>
      <Text {...textStyles[textStyleKeys.Body1_Bold]} color="fg.default">
        {title}
      </Text>
      <Text {...textStyles[textStyleKeys.Body3]} color="fg.muted">
        {description}
      </Text>
    </VStack>
  </VStack>
)

export type QueueMeta = {
  messages: {
    count: number
  }
  calls: {
    count: number
  }
}

// IMPORTANT: Popup should NOT run usePhoneEndpoint - it causes race conditions
export const PopupPage = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const isDevMode = searchParams.get('dev') === 'true'

  const [activeTab, setActiveTab] = useState<'calls' | 'messages'>('calls')
  const seenCallIds = useRef<Set<string>>(new Set()) // Track which calls we've already auto-switched for
  const userManuallyChangedTab = useRef(false) // Track if user manually changed tabs
  const previousMessageCount = useRef(0) // Track previous message count for detecting new messages
  const isPartner = useIsPartner()
  const showTexting = useShowTexting()
  const { sessionUser } = useUserState()

  // Save popup dimensions when window is resized. The main window owns the
  // saved value, so the main process forwards it there.
  const handleDimensionChange = useCallback(
    (dimensions: { width: number; height: number }) => {
      window.electron?.send('save-popup-dimensions', dimensions)
    },
    [],
  )
  useWindowDimensions(handleDimensionChange)

  const {
    settings: {
      webPhone: { ringtone, doNotDisturbTimestamp },
    },
  } = useSettingsState()
  const [messageQueue, setMessageQueue] = useSharedVariable<PopupQueueItem[]>(
    'MessageQueue',
    [],
  )
  const [sharedCallList] = useSharedVariable<PhoneCall[]>('CallList', [])

  const doNotDisturb: boolean = doNotDisturbTimestamp !== undefined
  const shouldRing = ringtone !== Ringtone.SILENT

  // Convert shared callList (PhoneCall[]) to CallQueueItem[] format for compatibility
  const callListAsQueueItems = useMemo<CallQueueItem[]>(() => {
    return (sharedCallList || []).map(
      (call) =>
        ({
          ...call,
          _id: call.callUuid,
          type: PopupQueueItemTypes.CALL,
        }) as CallQueueItem,
    )
  }, [sharedCallList])

  // Filter calls to show only inbound calls or unparked calls (not ENDED, ENDING_CALL, or regular outbound)
  // Also filter out ringing calls when DND is enabled (safety net - calls should be rejected immediately)
  const filteredCallQueue = useMemo(() => {
    return callListAsQueueItems.filter((call) => {
      // Filter out ended/ending calls
      if (
        call.status === PhoneCallStatus.ENDED ||
        call.status === PhoneCallStatus.ENDING_CALL
      ) {
        return false
      }

      // Filter out ringing calls when DND is enabled (safety net for race condition)
      if (
        doNotDisturb &&
        call.status != null &&
        isPreConnectedStatus(call.status)
      ) {
        return false
      }

      // Show inbound calls or unparked calls
      return call.direction === 'inbound' || call.isUnparked
    })
  }, [callListAsQueueItems, doNotDisturb])

  // Same rules as filteredCallQueue, except ended calls stay so they can be
  // announced as ended.
  const isCallShownInPopup = useCallback(
    (call: AnnouncedCall) =>
      !(doNotDisturb && isPreConnectedStatus(call.status)) &&
      (call.direction === 'inbound' || !!call.isUnparked),
    [doNotDisturb],
  )
  // While the main window leaves ringing calls out of the shared list (Do Not
  // Disturb), a vanished ringing call isn't proof it ended. The flag is
  // published before the list, so it doesn't depend on this window's own DND
  // setting arriving first.
  const [callListHidesRinging] = useSharedVariable<boolean>(
    'CallListHidesRinging',
    false,
  )
  useCallStateAnnouncements(sharedCallList || [], {
    isCallShown: isCallShownInPopup,
    announceUnansweredRemovals: !callListHidesRinging,
  })
  useRelayedCallAnnouncements()

  const hasCurrentCall = useMemo(() => {
    return (
      filteredCallQueue?.filter(
        (item: CallQueueItem) =>
          item.type === PopupQueueItemTypes.CALL &&
          item.status === PhoneCallStatus.CONNECTED &&
          item.isOnHold === false,
      ).length > 0
    )
  }, [filteredCallQueue])

  // Auto-load test data in dev mode
  useEffect(() => {
    if (isDevMode) {
      setMessageQueue(exampleTexts as PopupQueueItem[])
      // Note: test data for callList would need to be set via shared variable
      // For now, dev mode will use real callList
    }
  }, [isDevMode, setMessageQueue])

  // Clear message queue when user logs out (but not in dev mode)
  useEffect(() => {
    if (!sessionUser && !isDevMode) {
      setMessageQueue([])
    }
  }, [sessionUser, isDevMode, setMessageQueue])

  // Note: Call rejection logic is handled in the Main window's usePhone.ts
  // Both SIP INVITE and WebSocket paths check hasCallWaiting/isInQueue before adding calls to callList
  // PopupPage only displays calls that have already been accepted by the Main window

  useEffect(() => {
    // Auto-switch to calls tab only when a NEW call arrives
    const currentCallIds = new Set<string>(
      filteredCallQueue.map((call) => call._id as string),
    )

    // Check if there are any new calls we haven't seen before
    const hasNewCall = Array.from(currentCallIds).some(
      (id) => !seenCallIds.current.has(id),
    )

    if (hasNewCall && currentCallIds.size > 0) {
      // New call arrived - switch to calls tab
      setActiveTab('calls')
      // Reset manual flag since we're auto-switching
      userManuallyChangedTab.current = false
      // Update seen calls to current set
      seenCallIds.current = currentCallIds
    } else if (currentCallIds.size === 0) {
      // No calls anymore - clear the seen set
      seenCallIds.current.clear()
    } else {
      // Update seen set with current calls (handles call removals)
      seenCallIds.current = currentCallIds
    }
  }, [filteredCallQueue])

  // Auto-switch to messages tab when a NEW message arrives and there are no calls
  useEffect(() => {
    // Don't auto-switch if messaging is hidden
    if (!showTexting) {
      return
    }

    const currentMessageCount = messageQueue?.length || 0
    const hasCallItems = (filteredCallQueue?.length || 0) > 0

    // Check if this is a new message (count increased) and user hasn't manually changed tabs
    const isNewMessage = currentMessageCount > previousMessageCount.current

    // Auto-switch to messages tab if:
    // 1. A new message just arrived (count increased)
    // 2. We're on the calls tab (or this is initial load)
    // 3. There are no calls to show
    // 4. User hasn't manually switched tabs since last auto-switch
    if (isNewMessage && !hasCallItems && !userManuallyChangedTab.current) {
      setActiveTab('messages')
      // Reset manual flag since we're auto-switching
      userManuallyChangedTab.current = false
    }

    // Update previous count
    previousMessageCount.current = currentMessageCount
  }, [messageQueue, filteredCallQueue, showTexting])

  const queueMeta: QueueMeta = useMemo(() => {
    return {
      messages: {
        count: messageQueue?.length || 0,
      },
      calls: {
        count: filteredCallQueue?.length || 0,
      },
    }
  }, [filteredCallQueue, messageQueue])

  const scrollbarWidth = '12px'

  // Handle tab changes - track if user manually changed tabs
  const handleTabChange = (value: 'calls' | 'messages') => {
    userManuallyChangedTab.current = true
    setActiveTab(value)
  }

  // Ensure activeTab is 'calls' if messaging is hidden
  useEffect(() => {
    if (!showTexting && activeTab === 'messages') {
      setActiveTab('calls')
    }
  }, [showTexting, activeTab])

  // Determine if we should show tabs (only show if there are multiple tabs)
  const shouldShowTabs = showTexting

  return (
    <Flex direction="column" height="100vh" bg="bg.subtle">
      <PopupHeader meta={queueMeta} activeTab={activeTab} />

      <Tabs.Root
        value={activeTab}
        onValueChange={(details) => {
          if (details.value) {
            handleTabChange(details.value as 'calls' | 'messages')
          }
        }}
        display="flex"
        flexDirection="column"
        paddingLeft={6}
        paddingTop={6}
        paddingBottom={0}
        paddingRight={0} // Leaving room for scrollbar
        height="100%"
        position="relative"
        fitted
        overflow="hidden"
        variant="plain"
      >
        {shouldShowTabs && (
          <Tabs.List
            padding={2}
            bg="bg.default"
            gap={2}
            flexShrink={0}
            mr={6} // Scrollbar padding
            borderRadius="lg"
            border="1px solid"
            borderColor="border.default"
          >
            <StyledTab value="calls">
              {(filteredCallQueue?.length || 0) === 0 ? (
                <Icon as={Phone} color="fg.default" mr={2} />
              ) : (
                <CountBadge count={filteredCallQueue?.length || 0} />
              )}
              {t('popup.tabs.inboundCalls')}
            </StyledTab>
            <StyledTab value="messages">
              {(messageQueue?.length || 0) === 0 ? (
                <Icon as={MessageSquareText} color="currentColor" mr={2} />
              ) : (
                <CountBadge count={messageQueue?.length || 0} />
              )}
              {t('popup.tabs.messages')}
            </StyledTab>
          </Tabs.List>
        )}
        <Tabs.ContentGroup
          display={'flex'}
          flexDirection="column"
          height={'100%'}
          mt={shouldShowTabs ? 2 : 0}
          mb={isPartner ? 6 : 0}
          pr={2} // Scrollbar padding
          pb={2}
          zIndex={1}
          overflowY={'auto'}
          css={{
            scrollbarGutter: 'stable',
            '&::-webkit-scrollbar': {
              width: scrollbarWidth,
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'var(--chakra-colors-border-subtle)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'var(--chakra-colors-outline)',
            },
          }}
        >
          <Tabs.Content
            value="calls"
            display={'flex'}
            flexDirection="column"
            flex="1"
            flexGrow={1}
            padding={1}
            justifyContent={'space-between'}
          >
            {filteredCallQueue.length === 0 ? (
              <EmptyState
                icon={Phone}
                title={t('popup.emptyStates.noCalls.title')}
                description={t('popup.emptyStates.noCalls.description')}
              />
            ) : (
              (() => {
                // Filter to show all active calls - exclude only ENDED and ENDING_CALL
                // This ensures all calls in the queue are shown, matching the badge count
                const activeCalls = filteredCallQueue.filter((item) => {
                  if (item.type !== PopupQueueItemTypes.CALL) return false
                  const callItem = item as CallQueueItem
                  // Show all calls except those that are ended or ending
                  return (
                    callItem.status !== PhoneCallStatus.ENDED &&
                    callItem.status !== PhoneCallStatus.ENDING_CALL
                  )
                }) as CallQueueItem[]

                if (activeCalls.length === 0) {
                  return (
                    <Text
                      paddingTop={2}
                      {...textStyles[textStyleKeys.Body3]}
                      color="inherit"
                    >
                      {t('popup.emptyStates.noActiveCalls')}
                    </Text>
                  )
                }

                return <CallList callItems={activeCalls} />
              })()
            )}
          </Tabs.Content>
          {showTexting && (
            <Tabs.Content
              value="messages"
              display={'flex'}
              flexDirection="column"
              flex="1"
              flexGrow={1}
              padding={1}
              justifyContent={'space-between'}
            >
              {(messageQueue || []).length === 0 ? (
                <EmptyState
                  icon={MessageSquareX}
                  title={t('popup.emptyStates.noMessages.title')}
                  description={t('popup.emptyStates.noMessages.description')}
                />
              ) : (
                <MessageList
                  messageItems={
                    (messageQueue?.filter(
                      (item) => item.type === PopupQueueItemTypes.MESSAGE,
                    ) as MessageQueueItem[]) || []
                  }
                />
              )}
            </Tabs.Content>
          )}
        </Tabs.ContentGroup>

        {!isPartner && (
          <Flex
            position={'absolute'}
            bottom={0}
            left={0}
            right={0}
            padding={2}
            justifyContent={'center'}
          >
            <Text fontSize="x-small" color="fg.muted">
              {t('popup.footer.poweredByMango')}
            </Text>
          </Flex>
        )}
      </Tabs.Root>

      {/* Ring for any ringing call in the queue - but not when DND is enabled */}
      {!doNotDisturb &&
        filteredCallQueue.some(
          (item) =>
            item.type === PopupQueueItemTypes.CALL &&
            (item as CallQueueItem).status === PhoneCallStatus.RINGING,
        ) &&
        shouldRing && (
          <audio
            key={`ringtone-${hasCurrentCall}`}
            id="incoming-call-tone"
            src={TrimmedIncomingCallTone}
            autoPlay
            loop={!hasCurrentCall} // Only loop if there is no current call
          />
        )}
    </Flex>
  )
}
